import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaYoutube } from 'react-icons/fa';

const navLinks = [
  { to: '/', label: 'דף הבית' },
  { to: '/courses', label: 'קורסים' },
  { to: '/teachers', label: 'מורים' },
  { to: '/contact', label: 'צור קשר' },
];

const helpLinks = [
  { to: '/faq', label: 'שאלות נפוצות' },
  { to: '/support', label: 'תמיכה טכנית' },
  { to: '/terms', label: 'תנאי שימוש' },
  { to: '/privacy', label: 'מדיניות פרטיות' },
];

const FooterLink = ({ to, label }) => (
  <li>
    <Link
      to={to}
      className="hover:text-indigo-300 transition-colors duration-200 flex items-center gap-1.5 group"
    >
      <span className="w-1 h-1 rounded-full bg-indigo-500/40 group-hover:bg-indigo-400 transition-colors flex-shrink-0" />
      {label}
    </Link>
  </li>
);

const Footer = () => {
  const socialLinks = [
    { href: 'https://facebook.com', Icon: FaFacebookF, label: 'Facebook' },
    { href: 'https://instagram.com', Icon: FaInstagram, label: 'Instagram' },
    { href: 'https://youtube.com', Icon: FaYoutube, label: 'YouTube' },
  ];

  return (
    <footer
      className="relative bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-950 text-gray-400 overflow-hidden"
      dir="rtl"
    >
      {/* Top accent line */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-400/50 to-transparent" />

      {/* Background glows */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10">

        {/* ── Main grid ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 pb-8 border-b border-white/5">

          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-white font-bold text-base tracking-tight">דורוס אונליין</span>
            </div>
            <p className="text-sm leading-relaxed text-white/80 max-w-xs">
              פלטפורמה ללמידה מקוונת המאפשרת לתלמידים ומורים להתחבר, ללמד וללמוד מכל מקום.
            </p>
          </div>

          {/* ניווט */}
          <div>
            <h4 className="text-white/90 font-semibold text-xs uppercase tracking-widest mb-4">
              ניווט
            </h4>
            <ul className="space-y-2.5 text-sm">
              {navLinks.map((link) => (
                <FooterLink key={link.to} to={link.to} label={link.label} />
              ))}
            </ul>
          </div>

          {/* עזרה */}
          <div>
            <h4 className="text-white/90 font-semibold text-xs uppercase tracking-widest mb-4">
              עזרה
            </h4>
            <ul className="space-y-2.5 text-sm">
              {helpLinks.map((link) => (
                <FooterLink key={link.to} to={link.to} label={link.label} />
              ))}
            </ul>
          </div>

          {/* צור קשר */}
          <div className="col-span-2 md:col-span-1">
            <h4 className="text-white/90 font-semibold text-xs uppercase tracking-widest mb-4">
              צור קשר
            </h4>

            <ul className="space-y-2.5 text-sm">
              <li className="flex items-center gap-2">
                <svg
                  className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span>050-0000000</span>
              </li>
              <li className="flex items-center gap-2">
                <svg
                  className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span>support@doroosonline.com</span>
              </li>
            </ul>

            {/* Social icons */}
            <div className="flex gap-2 mt-5">
              {socialLinks.map(({ href, Icon, label }) => (
                <button
                  key={label}
                  type="button"
                  aria-label={label}
                  onClick={() => window.open(href, '_blank', 'noreferrer')}
                  className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 hover:bg-indigo-500/20 hover:border-indigo-400/30 hover:text-indigo-300 flex items-center justify-center text-sm transition-all duration-200"
                >
                  <Icon />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-4 text-xs text-gray-600">
          <span>© {new Date().getFullYear()} DoroosOnline. כל הזכויות שמורות.</span>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span>כל המערכות פעילות</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;